import React from 'react';
import { WebsiteDesignProps } from './types';
import { Anchor, Compass, Navigation } from 'lucide-react';
import { Button } from "@/components/ui/button";

const MaritimeInstitute: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Naval Academy";

    return (
        <div className="bg-[#0f172a] text-white min-h-screen font-sans">
            <nav className="border-b border-indigo-900/50 bg-[#0b1120] px-8 py-5 flex justify-between items-center">
                <div className="flex items-center gap-3 text-2xl font-bold tracking-widest uppercase text-white">
                    <Anchor className="text-cyan-400" /> {schoolName}
                </div>
                <div className="hidden md:flex gap-8 text-xs font-bold uppercase tracking-widest text-slate-400">
                    <a href="#" className="hover:text-cyan-400 transition-colors">Deck</a>
                    <a href="#" className="hover:text-cyan-400 transition-colors">Engine</a>
                    <a href="#" className="hover:text-cyan-400 transition-colors">Logistics</a>
                </div>
            </nav>

            <header className="relative py-24 px-8 overflow-hidden">
                {/* Sonar Effect */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-cyan-900/30 rounded-full"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-cyan-900/30 rounded-full"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-cyan-900/30 rounded-full"></div>

                <div className="container mx-auto relative z-10 text-center">
                    <div className="inline-block mb-6 border border-cyan-500/50 rounded-full px-4 py-1 text-cyan-400 text-xs font-mono uppercase tracking-widest">
                        Coordinates: 34° N, 118° W
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold mb-8 uppercase tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-500">
                        Command The <br/> Seas.
                    </h1>
                    <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto leading-relaxed">
                        World-class maritime education for the merchant navy and offshore industries. Discipline, leadership, and technical mastery.
                    </p>
                    <div className="flex justify-center gap-6">
                        <Button className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold uppercase tracking-wider px-8 h-12 rounded shadow-lg shadow-cyan-900/50">
                            Cadet Admission
                        </Button>
                    </div>
                </div>
            </header>

            <section className="bg-indigo-950/30 border-y border-indigo-900/50 py-16">
                <div className="container mx-auto px-8 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                    <div className="group cursor-pointer">
                        <div className="w-16 h-16 mx-auto bg-[#0f172a] border border-cyan-500/20 group-hover:border-cyan-400 rounded-full flex items-center justify-center text-cyan-400 mb-4 transition-colors">
                            <Navigation size={28} />
                        </div>
                        <h3 className="text-white font-bold uppercase tracking-widest mb-2">Navigation</h3>
                        <p className="text-slate-500 text-sm px-8">Celestial and electronic positioning systems mastery.</p>
                    </div>
                    <div className="group cursor-pointer">
                        <div className="w-16 h-16 mx-auto bg-[#0f172a] border border-cyan-500/20 group-hover:border-cyan-400 rounded-full flex items-center justify-center text-cyan-400 mb-4 transition-colors">
                            <Compass size={28} />
                        </div>
                        <h3 className="text-white font-bold uppercase tracking-widest mb-2">Safety</h3>
                        <p className="text-slate-500 text-sm px-8">SOLAS regulations and emergency response protocols.</p>
                    </div>
                    <div className="group cursor-pointer">
                        <div className="w-16 h-16 mx-auto bg-[#0f172a] border border-cyan-500/20 group-hover:border-cyan-400 rounded-full flex items-center justify-center text-cyan-400 mb-4 transition-colors">
                            <Anchor size={28} />
                        </div>
                        <h3 className="text-white font-bold uppercase tracking-widest mb-2">Seamanship</h3>
                        <p className="text-slate-500 text-sm px-8">The practical art of vessel operation and maintenance.</p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MaritimeInstitute;
